import { HttpClient } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { SignalStateService } from 'app/core/service/signal-state-service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { map, Observable, ReplaySubject, switchMap, take, tap } from 'rxjs';

interface NotificationsState {
    notifications: Notification[];
}
@Injectable({ providedIn: 'root' })
export class NotificationsService extends SignalStateService<NotificationsState> {
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
        super({
            notifications: [],
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications(): Signal<Notification[]> {
        return this.select('notifications');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]> {
        return this._httpClient.get<Notification[]>('/api/notifications').pipe(
            tap((notifications) => {
                this.setState({ notifications });
            })
        );
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification> {
        return this._httpClient
            .post<Notification>('/api/notifications', { notification })
            .pipe(
                tap((newNotification) => {
                    // Update the notifications with the new notification
                    this.setState({
                        notifications: [
                            ...this.select('notifications')(),
                            newNotification,
                        ],
                    });
                })
            );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification> {
        return this._httpClient
            .put<Notification>('/api/notifications', {
                id,
                notification,
            })
            .pipe(
                tap((updatedNotification: Notification) => {
                    // Update the notifications with the updated notification
                    this.setState({
                        notifications: this.select('notifications')().map(
                            (item) =>
                                item.id === id ? updatedNotification : item
                        ),
                    });
                })
            );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this._httpClient
            .delete<boolean>('/api/notifications', {
                params: { id },
            })
            .pipe(
                tap(() =>
                    // Update the notifications by removing the deleted notification
                    this.setState({
                        notifications: this.select('notifications')().filter(
                            (notification) => notification.id !== id
                        ),
                    })
                )
            );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean> {
        return this._httpClient
            .get<boolean>('/api/notifications/mark-all-as-read')
            .pipe(
                tap(() => {
                    this.setState({
                        notifications: this.select('notifications')().map(
                            (notification) => ({
                                ...notification,
                                read: true,
                            })
                        ),
                    })
                })
            );
    }
}
